import { ClassifierData } from 'types/classifiers';
import { MatterStateType } from 'types/matters';
import { FinishBlocker, TimecardListItemData, TimecardStatusType } from 'types/timecards';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { secondsToHours } from 'utils/time';

// Quarter-hour values in seconds
const QUARTER_HOUR_SECONDS = 900; // 0.25h
const THREE_QUARTER_HOUR_SECONDS = 2700; // 0.75h

/**
 * Converts seconds to hours, rounded up to decimal scale based on feature flag.
 *
 * When isTwoDecimalEnabled = true:
 * - Exact quarter-hour values (0.25h or 0.75h within any hour) are preserved
 * - All other values are rounded to 0.1h intervals, displayed with 2 decimals
 *
 * When isTwoDecimalEnabled = false:
 * - Standard 0.1h rounding
 *
 * @example
 * // returns 0.1 (when isTwoDecimalEnabled = false)
 * timecardSecondsToHours(360, false);
 * @example
 * // returns 0.1 (when isTwoDecimalEnabled = true, rounded to 0.1h interval)
 * timecardSecondsToHours(360, true);
 * @example
 * // returns 0.25 (when isTwoDecimalEnabled = true, exact quarter-hour preserved)
 * timecardSecondsToHours(900, true);
 * @example
 * // returns 0.3 (when isTwoDecimalEnabled = true, 901s rounds to 0.3h)
 * timecardSecondsToHours(901, true);
 *
 * @param seconds - duration in seconds
 * @param isTwoDecimalEnabled - when true, preserve quarter-hours and round others to 0.1h; when false, standard 0.1h rounding
 * @returns duration in hours
 */
export const timecardSecondsToHours = (seconds: number, isTwoDecimalEnabled: boolean): number => {
    if (seconds <= 0) {
        return 0;
    }

    if (isTwoDecimalEnabled) {
        // Check if this is an exact quarter-hour value (0.25h or 0.75h within any hour)
        const secondsWithinHour = seconds % 3600;
        if (secondsWithinHour === QUARTER_HOUR_SECONDS || secondsWithinHour === THREE_QUARTER_HOUR_SECONDS) {
            // Preserve exact quarter-hour value
            return secondsToHours(seconds, 2);
        }
        // All other values: round to 0.1h interval, display with 2 decimals
        const roundedSeconds = Math.ceil(seconds / 360) * 360;
        return secondsToHours(roundedSeconds, 2);
    }

    // Standard 1-decimal precision
    return secondsToHours(seconds, 1);
};

export const isTimecardSelectable = (timecard: TimecardListItemData) => {
    return (
        timecard.timecardStatusTypeCode === TimecardStatusType.FINISHED ||
        timecard.timecardStatusTypeCode === TimecardStatusType.DONE
    );
};

/**
 * Resolves stored {@code TIMECARD_VALUE_FACTORS} codes into the options a multiselect works in.
 *
 * getOptionsFromClassifier, not the "valid" variant: a factor already stored on a timecard must keep
 * displaying after it is retired from the picker.
 */
export const toValueFactorOptions = (
    codes: string[] | null | undefined,
    classifiers: Record<string, ClassifierData>
): { label: string; value: string }[] =>
    (getOptionsFromClassifier(classifiers['TIMECARD_VALUE_FACTORS']) as { label: string; value: string }[]).filter(
        (option) => codes?.includes(option.value)
    );

// A timecard left in DRAFT after its case moved past the active states can no longer be
// finished (the BE rejects it), only re-assigned to another case or deleted.
export const FINISH_BLOCKING_MATTER_STATES: string[] = [
    MatterStateType.COMPLETED,
    MatterStateType.OFFER_WON,
    MatterStateType.OFFER_LOST,
];

/** The sentence of each finish blocker, one a line in the tooltip of a disabled Finish. */
export const finishBlockerLines = (blockers: FinishBlocker[]): string[] =>
    blockers.map((blocker) => t(`timecards.finish-blockers.${blocker}`));
